import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";

// Lazily load the YouTube IFrame Player API exactly once for the whole app.
let ytApiPromise = null;
function loadYouTubeAPI() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === "function") {
        try { prev(); } catch { /* ignore */ }
      }
      resolve(window.YT);
    };
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(tag);
  });
  return ytApiPromise;
}

/**
 * Embeds a YouTube video via the IFrame Player API and reports playback time so
 * a parent can highlight a transcript in sync.
 *
 * Props:
 *   videoId       – 11-char YouTube id
 *   startSeconds  – initial seek offset (segment start in the source video)
 *   onTime(ms)    – called ~every pollMs while playing, with current time in ms
 *   onStateChange(state) – raw YT player state (1=playing, 2=paused, 0=ended…)
 *   pollMs        – time-report interval (default 150ms)
 *
 * Ref API: seekToMs(ms, play=true), play(), pause().
 */
const YouTubeSyncPlayer = forwardRef(function YouTubeSyncPlayer(
  { videoId, startSeconds = 0, onTime, onStateChange, pollMs = 150 },
  ref
) {
  const wrapperRef = useRef(null);
  const playerRef = useRef(null);
  const intervalRef = useRef(null);
  // Keep latest callbacks without re-creating the player.
  const onTimeRef = useRef(onTime);
  onTimeRef.current = onTime;
  const onStateRef = useRef(onStateChange);
  onStateRef.current = onStateChange;

  useImperativeHandle(
    ref,
    () => ({
      seekToMs(ms, play = true) {
        const p = playerRef.current;
        if (!p || !p.seekTo) return;
        p.seekTo(Math.max(0, ms / 1000), true);
        if (play && p.playVideo) p.playVideo();
      },
      play() { playerRef.current?.playVideo?.(); },
      pause() { playerRef.current?.pauseVideo?.(); },
    }),
    []
  );

  useEffect(() => {
    let cancelled = false;

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    const startPolling = () => {
      stopPolling();
      intervalRef.current = setInterval(() => {
        const p = playerRef.current;
        if (p && p.getCurrentTime) {
          onTimeRef.current?.(p.getCurrentTime() * 1000);
        }
      }, pollMs);
    };

    loadYouTubeAPI()
      .then((YT) => {
        if (cancelled || !wrapperRef.current) return;
        // Give the API its own node so React never reconciles the iframe.
        const host = document.createElement("div");
        host.className = "w-full h-full";
        wrapperRef.current.appendChild(host);
        playerRef.current = new YT.Player(host, {
          videoId,
          playerVars: {
            start: Math.max(0, Math.floor(startSeconds || 0)),
            rel: 0,
            modestbranding: 1,
            playsinline: 1,
          },
          events: {
            onStateChange: (e) => {
              onStateRef.current?.(e.data);
              if (e.data === 1) startPolling();
              else stopPolling();
            },
          },
        });
      })
      .catch(() => { /* API failed to load; player simply absent */ });

    return () => {
      cancelled = true;
      stopPolling();
      try { playerRef.current?.destroy?.(); } catch { /* ignore */ }
      playerRef.current = null;
    };
    // Recreate only when the video changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  return (
    <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-black shadow-soft">
      <div ref={wrapperRef} className="absolute inset-0 w-full h-full" />
    </div>
  );
});

export default YouTubeSyncPlayer;
